import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useNetworkIdRedirect } from "./common/hooks";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import { DownloadDataUI } from "components/gateways/DownloadDataUI";
import { 
    getOneRegistry, 
    getRegistryConfig, 
    getGwAndLDSUStatus 
} from "service/gatewayService";
import { 
    getAuxiliaryHubsFromLdsus
} from "./ldsuTransformers";
import { isHttpSuccess } from "utils/functions";
import { BREADCRUMB_LABELS } from "./constants";
import { DynamicBreadcrumb } from "./common/DynamicBreadcrumb";

type AuxiliaryHub = {
    name: string;
    uuid: string;
    serialNumber: string;
    productName: string;
    manufacturingDate: string;
    productVersion: string;
    firmwareVersion: string;
    ldsuDid: string;
    terminationStatus: string;
    status?: string;
    type?: string;
    hub_type?: string;
    lastActive?: string;
    networkName?: string;
};

const AuxiliaryHubDetails = (): JSX.Element => {
    const { id, auxiliaryHubId } = useParams<{ id: string; auxiliaryHubId: string }>();
    const { validateAndRedirect } = useNetworkIdRedirect(id);
    const [auxiliaryHub, setAuxiliaryHub] = useState<AuxiliaryHub | null>(null);
    const [hubName, setHubName] = useState("");
    const [isHubNameEditing, setIsHubNameEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!validateAndRedirect()) {
            return;
        }
        const fetchAuxiliaryHubData = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const ldsuGatewayId = id; // Use panlNetworkId from route params

                // Try to fetch real LDSU data first using the specific gateway ID
                const [registryInfoRes, registryConfigRes, registryGwAndLdsuStatusRes] = await Promise.all([
                    getOneRegistry(ldsuGatewayId),
                    getRegistryConfig(ldsuGatewayId),
                    getGwAndLDSUStatus(ldsuGatewayId)
                ]);

                if (isHttpSuccess(registryInfoRes.status) && isHttpSuccess(registryConfigRes.status) && isHttpSuccess(registryGwAndLdsuStatusRes.status)) {
                    // Get all LDSUs from all buses and transform them
                    const allAuxiliaryHubs = [];
                    
                    // Process each bus (assuming 3 buses as in the original code)
                    for (let busNumber = 1; busNumber <= 3; busNumber++) {
                        const auxiliaryHubs = getAuxiliaryHubsFromLdsus(
                            busNumber.toString(),
                            registryInfoRes.data?.info?.devices || [],
                            registryConfigRes.data || [],
                            registryGwAndLdsuStatusRes.data?.devices || []
                        );
                        allAuxiliaryHubs.push(...auxiliaryHubs);
                    }

                    // Find the specific LDSU by ID
                    const targetLdsu = allAuxiliaryHubs.find(hub => 
                        hub.uuid === auxiliaryHubId || 
                        hub.name === auxiliaryHubId
                    );

                    if (targetLdsu) {
                        setAuxiliaryHub(targetLdsu);
                        setHubName(targetLdsu.name);
                        setIsLoading(false);
                        return;
                    }
                }

                // No matching auxiliary hub found - set to null to show error state
                setAuxiliaryHub(null);

            } catch (err) {
                setError("Failed to load auxiliary hub data");
                console.error("Error fetching auxiliary hub data:", err);
            } finally {
                setIsLoading(false);
            }
        };

        if (id && id !== 'undefined' && auxiliaryHubId) {
            fetchAuxiliaryHubData();
        }
    }, [id, auxiliaryHubId, validateAndRedirect]);

    const toggleHubNameEdit = () => {
        setIsHubNameEditing(!isHubNameEditing);
    };

    const saveHubName = () => {
        setIsHubNameEditing(false);
        if (auxiliaryHub) {
            setAuxiliaryHub({ ...auxiliaryHub, name: hubName });
        }
    };

    if (!auxiliaryHub) {
        let statusMessage;

        if (isLoading) {
            statusMessage = <p>Loading auxiliary hub data...</p>;
        } else if (error) {
            statusMessage = <p>Error: {error}</p>;
        } else {
            statusMessage = <p>Auxiliary Hub not found or loading...</p>;
        }

        return (
            <ContentWrapper>
                <div className="page-content">
                    <Container fluid>
                        <Row>
                            <Col sm={12}>
                                <h5 className="page-title">Auxiliary Hub Details</h5>
                                <DynamicBreadcrumb currentPageLabel={BREADCRUMB_LABELS["auxiliary-hub-details"]} />
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={8}>
                                <div
                                    className="auxiliary-hub-not-found-panel"
                                    style={{
                                        backgroundColor: '#2f3a51',
                                        borderRadius: '8px',
                                        padding: '24px',
                                        marginBottom: '20px',
                                    }}
                                >
                                    {statusMessage}
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </ContentWrapper>
        );
    }

    return (
        <ContentWrapper>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col sm={12}>
                            <h5 className="page-title">{auxiliaryHub.name}</h5>
                            <DynamicBreadcrumb currentPageLabel={BREADCRUMB_LABELS["auxiliary-hub-details"]} />
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={12} xs={12}>
                            {/* Auxiliary Hub Name Section */}
                            <div className="form-box mt-2">
                                <Form.Row>
                                    <Col md="12">
                                        <Form.Label>Auxiliary Hub Name</Form.Label>
                                    </Col>
                                    <Col lg={10} xs={12} className="mb-2">
                                        <Form.Group className="mb-0">
                                            <Form.Control
                                                type="text"
                                                required
                                                aria-label="name"
                                                disabled={!isHubNameEditing}
                                                value={hubName}
                                                onChange={(e) => {
                                                    setHubName(e.target.value);
                                                }}
                                                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        saveHubName();
                                                    }
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col lg={2} xs={12}>
                                        {isHubNameEditing ? (
                                            <Button
                                                type="submit"
                                                className="w-75 ml-5"
                                                onClick={saveHubName}
                                            >
                                                Save
                                            </Button>
                                        ) : (
                                            <Button
                                                type="button"
                                                className="w-75 ml-5"
                                                disabled={true}
                                                onClick={toggleHubNameEdit}
                                            >
                                                EDIT
                                            </Button>
                                        )}
                                    </Col>
                                </Form.Row>
                            </div>
                        </Col>
                    </Row>

                    <Row className="flex-row">
                        <Col lg={6} xs={12}>
                            {/* Auxiliary Hub Details Section */}
                            <div className="form-box mt-2 pb-5">
                                <Form.Row>
                                    <Col md="12">
                                        <div className="descriptor-item">
                                            <h6 className="float-left">UUID</h6>
                                            <h6 className="ginfo float-right">
                                                {auxiliaryHub.uuid}
                                            </h6>
                                        </div>
                                        <div className="descriptor-item">
                                            <h6 className="float-left">Product Name</h6>
                                            <h6 className="ginfo float-right">
                                                {auxiliaryHub.productName}
                                            </h6>
                                        </div>
                                    </Col>
                                </Form.Row>
                            </div>
                        </Col>

                        <Col lg={6} xs={12}>
                            {/* Download Section */}
                            <div className="form-box download-auxiliary-hub-data mt-2">
                                <h5>Download auxiliary hub data</h5>
                                <DownloadDataUI
                                    gatewayId={id}
                                    ldsuId={auxiliaryHub?.uuid}
                                    downloadType="downloadAuxiliaryHub"
                                    emailLabel="Recieve Link"
                                />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </ContentWrapper>
    );
};

export default AuxiliaryHubDetails; 